// src/queue/queue.ts
// THE OFFLINE QUEUE. IndexedDB-backed, survives a reload and a force-quit, replays with the SAME
// idempotency keys so a replay writes nothing new.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHAT IS ACTUALLY QUEUED
// ══════════════════════════════════════════════════════════════════════════════════════════════
// Two job kinds, because the backend gives exactly two things an idempotency key:
//   · `session` — a practice session, keyed `(client_device_id, client_session_uuid)`
//   · `upload`  — a video, keyed `client_upload_uuid`
// The keys are minted at ENQUEUE time and stored ON the job. They are never recomputed. See
// device/ids.ts for why that single sentence is the entire correctness argument.
//
// An `upload` job holds the File the user picked. That is a `File` (a `Blob`), which IndexedDB
// stores natively — the reason the queue could never have been localStorage.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE STATE MACHINE, AND THE ONE STATE MOST QUEUES GET WRONG
// ══════════════════════════════════════════════════════════════════════════════════════════════
//     queued ──▶ uploading ──▶ done
//        ▲           │
//        └───────────┴──▶ failed        (a real, named reason — never "error")
//                    └──▶ held          (a person must act; retrying changes nothing)
//
// `held` is the one that matters. A DARK deployment answers 503 `r2_not_configured`, a stopped lab
// answers 503 `practice_lab_unavailable`, and an undeployed build answers 404 `route_not_mounted`.
// A queue that treats those as transient spins forever and shows a spinner that never resolves —
// the precise failure mode the brief forbids. Those three go to `held`, with the server's own
// sentence stored on the job, and the queue STOPS touching them until a person says try again.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// CRASH SAFETY
// ══════════════════════════════════════════════════════════════════════════════════════════════
// A job is written to IDB BEFORE any network call, and every transition is written through
// `idbUpdate` (one transaction, read-modify-write). A force-quit mid-flight therefore leaves a job
// in `uploading` — which `recoverStaleJobs()` resets to `queued` on the next boot, because a job
// stuck in a state no one is working on is a job that never runs again. It is safe to reset: the
// replay is idempotent, which is the entire design.

import { STORE, idbAll, idbClear, idbDelete, idbGet, idbPut, idbUpdate } from '../store/idb';
import { deviceId, newSessionUuid, newUploadUuid } from '../device/ids';
import { createSession, type CameraRole, type CreateSessionBody } from '../api/practice';
import { isRefusal } from '../api/client';
import { neverSucceedsOnRetry, type Refusal } from '../api/refusal';
import { contentTypeFor, runUpload } from '../upload/multipart';

export type JobState = 'queued' | 'uploading' | 'done' | 'failed' | 'held';

interface BaseJob {
  id: string;
  kind: 'session' | 'upload';
  state: JobState;
  athlete_id: string;
  created_at: number;
  updated_at: number;
  attempts: number;
  /** The server's own sentence for the last refusal. NEVER a message this client invented. */
  last_error: string | null;
  /** The machine-readable kind, so the UI can style a `held` differently from a `failed`. */
  last_error_kind: string | null;
  last_error_detail: unknown;
  /** True when the server said `replayed` — proof the replay wrote nothing new. */
  replayed: boolean | null;
}

export interface SessionJob extends BaseJob {
  kind: 'session';
  /** Minted ONCE, at enqueue. Never regenerated. */
  client_session_uuid: string;
  client_device_id: string;
  body: CreateSessionBody;
  /** The row id the server returned, once it has. */
  server_session_id: string | null;
}

export interface UploadJob extends BaseJob {
  kind: 'upload';
  /** Minted ONCE, at enqueue. Never regenerated. */
  client_upload_uuid: string;
  file: Blob;
  file_name: string;
  content_type: string;
  total_bytes: number;
  session_id: string | null;
  camera_role: CameraRole | null;
  /** Straight from the server's `safe_to_delete_local_bytes`. Never computed here. */
  safe_to_delete_local_bytes: number | null;
  parts_acknowledged: number;
  parts_total: number;
  media_id: string | null;
}

export type Job = SessionJob | UploadJob;

type Listener = (jobs: Job[]) => void;
const listeners = new Set<Listener>();

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  void listJobs().then(fn);
  return () => listeners.delete(fn);
}

async function notify(): Promise<void> {
  const jobs = await listJobs();
  for (const fn of listeners) fn(jobs);
}

export async function listJobs(): Promise<Job[]> {
  const jobs = await idbAll<Job>(STORE.jobs);
  return jobs.sort((a, b) => a.created_at - b.created_at);
}

export const getJob = (id: string): Promise<Job | undefined> => idbGet<Job>(STORE.jobs, id);

function jobId(): string {
  return `job-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * ENQUEUE — the keys are minted HERE and only here
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export async function enqueueSession(
  athleteId: string,
  input: Omit<CreateSessionBody, 'client_device_id' | 'client_session_uuid'>,
): Promise<SessionJob> {
  const device = await deviceId();
  const uuid = newSessionUuid();
  const job: SessionJob = {
    id: jobId(),
    kind: 'session',
    state: 'queued',
    athlete_id: athleteId,
    created_at: Date.now(),
    updated_at: Date.now(),
    attempts: 0,
    last_error: null,
    last_error_kind: null,
    last_error_detail: null,
    replayed: null,
    client_session_uuid: uuid,
    client_device_id: device,
    body: { ...input, client_device_id: device, client_session_uuid: uuid },
    server_session_id: null,
  };
  await idbPut(STORE.jobs, job);
  await notify();
  return job;
}

export async function enqueueUpload(args: {
  athleteId: string;
  file: File | (Blob & { name?: string });
  sessionId?: string | null;
  cameraRole?: CameraRole | null;
}): Promise<UploadJob> {
  const job: UploadJob = {
    id: jobId(),
    kind: 'upload',
    state: 'queued',
    athlete_id: args.athleteId,
    created_at: Date.now(),
    updated_at: Date.now(),
    attempts: 0,
    last_error: null,
    last_error_kind: null,
    last_error_detail: null,
    replayed: null,
    client_upload_uuid: newUploadUuid(),
    file: args.file,
    file_name: (args.file as { name?: string }).name ?? 'capture',
    content_type: contentTypeFor(args.file as { type?: string; name?: string }),
    total_bytes: args.file.size,
    session_id: args.sessionId ?? null,
    camera_role: args.cameraRole ?? null,
    safe_to_delete_local_bytes: null,
    parts_acknowledged: 0,
    parts_total: 0,
    media_id: null,
  };
  await idbPut(STORE.jobs, job);
  await notify();
  return job;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * TRANSITIONS
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

async function patch(id: string, fn: (j: Job) => Job): Promise<void> {
  await idbUpdate<Job>(STORE.jobs, id, (cur) => (cur ? { ...fn(cur), updated_at: Date.now() } : null));
  await notify();
}

/**
 * Record a refusal against a job, choosing `held` vs `failed` HONESTLY.
 *
 * The distinction is not cosmetic. `held` means "no amount of retrying by this device will change
 * the answer — a person with access to the deployment must act", and the queue will not touch the
 * job again on its own. `failed` means "this could work on the next attempt". Collapsing the two is
 * how a DARK deployment produces an app that appears to be trying forever.
 */
function applyRefusal(job: Job, r: Refusal): Job {
  return {
    ...job,
    // `neverSucceedsOnRetry`, not `needsOperator`: a 400 on a FROZEN payload is just as permanent
    // as a missing bucket. Treating it as `failed` meant drain() re-sent it on every pass — the
    // founder's screen showed one job at 84 attempts, all refused for the same reason.
    state: neverSucceedsOnRetry(r) ? 'held' : 'failed',
    // The SERVER's sentence. If it wrote one, it is what the athlete reads.
    last_error: r.message,
    last_error_kind: r.kind,
    last_error_detail: {
      status: r.status,
      reason: r.reason,
      switch_name: r.switchName,
      missing: r.missing,
      note: r.note,
      request: r.request,
    },
  };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * RUN
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

async function runSessionJob(job: SessionJob): Promise<void> {
  await patch(job.id, (j) => ({ ...j, state: 'uploading', attempts: j.attempts + 1 }));
  // NOTE the body: `job.body` was built at enqueue time and is sent BYTE-IDENTICAL on every attempt.
  // Rebuilding it here — and re-minting the uuid inside it — is the one change that would break the
  // idempotency contract while leaving every test that only checks "it eventually succeeds" green.
  const res = await createSession(job.athlete_id, job.body);
  if (isRefusal(res)) {
    await patch(job.id, (j) => applyRefusal(j, res));
    return;
  }
  await patch(job.id, (j) => ({
    ...j,
    state: 'done',
    last_error: null,
    last_error_kind: null,
    last_error_detail: null,
    replayed: res.value.replayed,
    ...(j.kind === 'session' ? { server_session_id: res.value.session?.id ?? null } : {}),
  }));
}

async function runUploadJob(job: UploadJob): Promise<void> {
  await patch(job.id, (j) => ({ ...j, state: 'uploading', attempts: j.attempts + 1 }));
  const out = await runUpload({
    athleteId: job.athlete_id,
    clientUploadUuid: job.client_upload_uuid, // ← the SAME key on every replay. Never re-minted.
    file: job.file as Blob & { name?: string },
    contentType: job.content_type,
    ...(job.session_id ? { sessionId: job.session_id } : {}),
    ...(job.camera_role ? { cameraRole: job.camera_role } : {}),
    onProgress: (p) => {
      void idbUpdate<Job>(STORE.jobs, job.id, (cur) =>
        cur && cur.kind === 'upload'
          ? { ...cur, parts_acknowledged: p.partsAcknowledged, parts_total: p.partsTotal,
              safe_to_delete_local_bytes: p.safeToDeleteLocalBytes, updated_at: Date.now() }
          : (cur ?? null));
    },
  });

  if (!out.ok) {
    await patch(job.id, (j) => applyRefusal(j, out));
    return;
  }
  await patch(job.id, (j) => ({
    ...j,
    state: 'done',
    last_error: null,
    last_error_kind: null,
    last_error_detail: null,
    replayed: out.replayed,
    ...(j.kind === 'upload' ? { media_id: out.mediaId } : {}),
  }));
}

let draining = false;

/**
 * Replay everything runnable, oldest first.
 *
 * SERIAL, not parallel. Two reasons: a phone on a bar of signal that fans out five 8 MB PUTs
 * finishes none of them, and the order sessions land in is the order a coach created them.
 *
 * REENTRANCY-GUARDED. `visibilitychange`, `online` and a manual tap can all fire within the same
 * second; without the guard the same job runs twice concurrently. The server's idempotency makes
 * that harmless to the DATA — but the second run would still push the same bytes over the same
 * scarce connection, which is the resource the whole design is trying to protect.
 */
export async function drain(): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    for (const job of await listJobs()) {
      // `held` is skipped deliberately — see the state machine note. It is resumed only by
      // `retry()`, which is a person tapping "try again".
      if (job.state !== 'queued' && job.state !== 'failed') continue;
      if (job.kind === 'session') await runSessionJob(job);
      else await runUploadJob(job);
    }
  } finally {
    draining = false;
  }
}

/** A person says "try again" — including for a `held` job, e.g. after the Founder configures R2. */
export async function retry(id: string): Promise<void> {
  await patch(id, (j) => ({ ...j, state: 'queued', last_error: null, last_error_kind: null }));
  await drain();
}

export async function removeJob(id: string): Promise<void> {
  await idbDelete(STORE.jobs, id);
  await notify();
}

export async function clearDone(): Promise<void> {
  for (const j of await listJobs()) if (j.state === 'done') await idbDelete(STORE.jobs, j.id);
  await notify();
}

/** Test-only. */
export async function __clearAll(): Promise<void> {
  await idbClear(STORE.jobs);
  await notify();
}

/**
 * Reset jobs left mid-flight by a crash, force-quit or tab close.
 *
 * A job in `uploading` with nobody working on it is stranded: `drain()` skips it forever. Resetting
 * it to `queued` is safe precisely because the replay is idempotent — the server recognises the key,
 * returns `replayed: true`, and writes nothing a second time. Call this once at boot.
 */
export async function recoverStaleJobs(): Promise<number> {
  let n = 0;
  for (const job of await listJobs()) {
    if (job.state !== 'uploading') continue;
    await patch(job.id, (j) => ({
      ...j,
      state: 'queued',
      last_error: 'Interrupted before it finished — the app was closed or reloaded. It will carry on '
        + 'from the chunks already uploaded.',
      last_error_kind: 'interrupted',
    }));
    n += 1;
  }
  return n;
}

export interface QueueCounts {
  queued: number; uploading: number; failed: number; held: number; done: number; total: number;
}

/** Counts for the header badge. Every state is named — there is no "other" bucket. */
export function countJobs(jobs: Job[]): QueueCounts {
  const c: QueueCounts = { queued: 0, uploading: 0, failed: 0, held: 0, done: 0, total: jobs.length };
  for (const j of jobs) c[j.state] += 1;
  return c;
}

/**
 * Start the queue: recover stranded jobs, then drain on every event that plausibly means "there is
 * signal now". `online` alone is not enough — iOS Safari fires it unreliably, and a backgrounded tab
 * that comes forward is the far more common moment a coach actually has a connection.
 */
export function startQueue(): void {
  void recoverStaleJobs().then(() => drain());
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => void drain());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void drain();
  });
}
